import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import AppInput from "@src/components/AppInput";
import TimesIcon from "@assets/img/times-icon.svg";
import RightArrow from "@assets/img/right-arrow.svg";
import { theme } from "@src/theme";
import styled from "styled-components";
import Spinner from "@src/components/Spinner";
import { useMyShows } from "@src/hooks/useMyShows";
import AppSelectDropdown from "@src/components/AppSelectDropdown";
import { useMyCurators } from "@src/hooks/useMyCurators";
import { useAuthCurator, useRole } from "@src/hooks/useAuth";
import Tooltip from "@src/components/Tooltip";
import CuratorDropdownOption from "@src/components/CuratorDropdownOption";
import { IGuestCuratorRecord } from "@src/types/curator";
import { AppSelectDropdownOption } from "@src/components/AppSelectDropdown/AppSelectDropdown";
import { useInfiniteFactoryMoods } from "@src/queries/moodQueries";
import { IMoodFactory } from "@src/types/mood";
import {
  useHasVisibleJinglesQuery,
  useShowJingleQuery,
  useVisibleJinglesQuery,
} from "@src/queries/jingleQueries";
import { FEATURES } from "@src/constants";

const ActionsButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 50px;
  margin-bottom: ${theme.margins.medium};
`;

const CloseButton = styled.button`
  background-color: ${theme.colors.gray300};
  color: ${theme.colors.yellow};
  border: 1px solid ${theme.colors.yellow};
  min-width: 30px;
  min-height: 30px;
  border-radius: 15px;
  cursor: pointer;
`;
const SubmitButton = styled.button`
  opacity: ${({ disabled }): string => (disabled ? "0.5" : "1")};
  background-color: ${theme.colors.gray300};
  color: ${theme.colors.yellow};
  border: 1px solid ${theme.colors.yellow};
  width: 65px;
  height: 30px;
  border-radius: 15px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const schema = yup
  .object({
    assignedShow: yup.string().when("$isMoodFactory", {
      is: true,
      then: (s) => s.notRequired(),
      otherwise: (s) => s.required("Show is required"),
    }),

    moodFactory: yup.string().when("$isMoodFactory", {
      is: true,
      then: (s) => s.required("Mood is required"),
      otherwise: (s) => s.notRequired(),
    }),

    playlistName: yup
      .string()
      .max(255, "Playlist name cannot exceed 255 characters")
      .required("Playlist name is required"),

    description: yup
      .string()
      .max(75, "Description cannot exceed 75 characters")
      .notRequired(),
    assignedGuest: yup.string().when(["$roleType", "$isEditing"], {
      is: (roleType: string, isEditing: boolean) =>
        !isEditing && (roleType === "admin" || roleType === "manager"),
      then: (s) => s.required("Curator is required"),
      otherwise: (s) => s.notRequired(),
    }),
    notes: yup.string().notRequired(),
    // Optional by product decision: a playlist can be created without a jingle
    // and have one attached later from the playlist details screen.
    jingle: yup.string().notRequired(),
  })
  .required();

export type AddNewPlaylistFormValues = {
  assignedShow?: string;
  playlistName: string;
  description?: string | null;
  assignedGuest?: string | null;
  notes?: string | null;
  moodFactory?: string;
  // Nullable like the other optional strings: yup infers `Maybe<string>` for a
  // notRequired() field.
  jingle?: string | null;
};
type AddNewPlaylistFormProps = {
  closeButtonHandler: () => void;
  submitButtonHandler: (data: AddNewPlaylistFormValues) => void;
  successCreatingPlaylists: boolean;
  creatingPlaylist: boolean;
  initialState?: Partial<AddNewPlaylistFormValues>;
  isMoodFactory?: boolean;
};
const defaultValues = {
  assignedShow: "",
  playlistName: "",
  description: "",
  guest: "",
  assignedGuest: "",
  notes: "",
  moodFactory: "",
  jingle: "",
};

const AddNewPlaylistForm: React.FC<AddNewPlaylistFormProps> = ({
  creatingPlaylist,
  successCreatingPlaylists,
  closeButtonHandler,
  submitButtonHandler,
  initialState,
  isMoodFactory,
}) => {
  const curator = useAuthCurator();
  const roleType = useRole();
  const [searchText, setSearchText] = React.useState("");
  const [moodFactoryFilter, setMoodFactoryFilter] = useState({
    searchText: "",
  });
  const [showFilter, setShowFilter] = useState({
    searchText: "",
  });
  const { myShows, loadingMyShows } = useMyShows({
    curatorId: curator!.id,
    roleType,
    searchText: showFilter.searchText,
    isMoodFactory,
  });
  const { data: moodFactories } = useInfiniteFactoryMoods({
    searchText: moodFactoryFilter.searchText,
  });
  const moodFactoryOptions = moodFactories
    ? moodFactories.pages.flatMap(
        (page) => (page as { data: { data: IMoodFactory[] } }).data?.data
      )
    : [];
  const role = useRole();
  const [searchGuest, setSearchGuest] = useState("");
  const [guestFilter, setGuestFilter] = useState({
    searchText: "",
  });

  const [searchJingle, setSearchJingle] = useState("");
  const [jingleDebouncedSearch, setJingleDebouncedSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setJingleDebouncedSearch(searchJingle);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchJingle]);

  const jingleFeatureActive = FEATURES.PLAYLIST_JINGLE && !isMoodFactory;

  type SanitizedAddNewPlaylistFormValues = {
    assignedShow?: string;
    description?: string;
    assignedGuest?: string;
    notes?: string;
    moodFactory?: string;
    playlistName?: string;
    jingle?: string;
  };

  const sanitizeInitialState = (
    state?: Partial<AddNewPlaylistFormValues>
  ): Partial<SanitizedAddNewPlaylistFormValues> => {
    if (!state) return {};
    return {
      assignedShow: state.assignedShow,
      playlistName: state.playlistName,
      description: state.description === null ? undefined : state.description,
      assignedGuest:
        state.assignedGuest === null ? undefined : state.assignedGuest,
      notes: state.notes === null ? undefined : state.notes,
      moodFactory: state.moodFactory === null ? undefined : state.moodFactory,
      jingle: state.jingle === null ? undefined : state.jingle,
    };
  };

  useEffect(() => {
    if (searchText === moodFactoryFilter.searchText) return;
    const handler = setTimeout(() => {
      setMoodFactoryFilter({ searchText });
    }, 800);
    return (): void => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    if (searchText === showFilter.searchText) return;
    const handler = setTimeout(() => {
      setShowFilter({ searchText });
    }, 800);
    return (): void => clearTimeout(handler);
  }, [searchText]);

  useEffect(() => {
    if (searchGuest === guestFilter.searchText) return;
    const handler = setTimeout(() => {
      setGuestFilter({ searchText: searchGuest });
    }, 800);
    return (): void => clearTimeout(handler);
  }, [searchGuest]);

  const { myCurators, fetchingMyCurators } = useMyCurators(
    curator?.documentId || "",
    guestFilter.searchText,
    role
  );
  const {
    watch,
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    context: {
      isMoodFactory: !!isMoodFactory,
      roleType,
      isEditing: !!initialState?.playlistName,
    },
    defaultValues: { ...defaultValues, ...sanitizeInitialState(initialState) },
  });

  const assignedShow = watch("assignedShow");
  const selectedJingle = watch("jingle");

  // The show's own jingle is the automatic fallback for a playlist without one,
  // so it is left out of the picker rather than offered as a choice.
  const { data: showJingleId, isLoading: loadingShowJingle } =
    useShowJingleQuery(assignedShow, jingleFeatureActive);

  // Hold the jingle list back until the exclusion is known, otherwise the show's
  // jingle flashes in the dropdown for the round-trip before we can drop it.
  const jingleListEnabled =
    jingleFeatureActive && (!assignedShow || !loadingShowJingle);

  const { data: jingles, isFetching: fetchingJingles } = useVisibleJinglesQuery(
    jingleDebouncedSearch,
    jingleListEnabled,
    showJingleId
  );

  // When there is nothing left to offer — every jingle is `visible: false`, or
  // the only visible one is the show's — don't offer the jingle option at all.
  const { data: hasVisibleJingles } = useHasVisibleJinglesQuery(
    jingleListEnabled,
    showJingleId
  );

  // Switching shows can strand a pick that is now the new show's own jingle;
  // drop it so the show jingle is never submitted as an explicit choice.
  useEffect(() => {
    if (showJingleId && selectedJingle === showJingleId) {
      setValue("jingle", "");
    }
  }, [showJingleId, selectedJingle]);

  useEffect(() => {
    if (!loadingMyShows && myShows.length > 0) {
      if (
        !initialState?.assignedShow &&
        myShows.length === 1 &&
        roleType === "curator"
      ) {
        reset({
          ...defaultValues,
          ...sanitizeInitialState(initialState),
          assignedShow: myShows[0].documentId,
        });
      } else {
        reset({ ...defaultValues, ...sanitizeInitialState(initialState) });
      }
    }
  }, [initialState, loadingMyShows, myShows, roleType]);

  useEffect(() => {
    if (!successCreatingPlaylists) {
      reset({ ...defaultValues });
    }
  }, [successCreatingPlaylists]);

  const onSubmit = async (data: AddNewPlaylistFormValues): Promise<void> => {
    if (creatingPlaylist) return;
    await submitButtonHandler(data as AddNewPlaylistFormValues);
  };

  useEffect(() => {
    if (
      myShows.length === 1 &&
      roleType === "curator" &&
      !watch("assignedShow")
    ) {
      reset({
        ...defaultValues,
        ...sanitizeInitialState(initialState),
        assignedShow: myShows[0].documentId,
      });
    }
  }, [myShows, roleType]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ActionsButtonGroup>
        <Tooltip key={"cancel-tooltip"} content="Cancel">
          <CloseButton
            type="button"
            onClick={() => {
              closeButtonHandler();
              reset({
                ...defaultValues,
                ...sanitizeInitialState(initialState),
              });
            }}
          >
            <img
              src={TimesIcon}
              alt="X"
              style={{ width: "10px", height: "10px" }}
            />
          </CloseButton>
        </Tooltip>
        {initialState?.playlistName && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              maxWidth: 250,
            }}
          >
            <p
              style={{
                margin: 0,
                fontWeight: "bold",
                fontSize: "1.2rem",
                transition: "all 0.3s ease",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                color: theme.colors.yellow,
              }}
            >
              {initialState?.playlistName}
            </p>
          </div>
        )}
        {creatingPlaylist ? (
          <div style={{ width: 65, height: 30 }}>
            <Spinner small />
          </div>
        ) : (
          <Tooltip
            key={"submit-tooltip"}
            content={
              initialState?.playlistName ? "Update Playlist" : "Create Playlist"
            }
          >
            <SubmitButton
              disabled={creatingPlaylist || successCreatingPlaylists}
              type="submit"
            >
              <img
                src={RightArrow}
                alt="Submit"
                style={{ height: "50%", objectFit: "contain" }}
              />
            </SubmitButton>
          </Tooltip>
        )}
      </ActionsButtonGroup>
      {!isMoodFactory && (
        <div style={{ marginBottom: theme.margins.medium }}>
          <Controller
            name="assignedShow"
            control={control}
            render={({ field }) => (
              <AppSelectDropdown
                searching={loadingMyShows}
                setSearchText={setSearchText}
                searchText={searchText}
                options={myShows}
                labelAccessor="title"
                valueAccessor="documentId"
                successStyle={successCreatingPlaylists}
                label={"Show"}
                control={control}
                errors={errors}
                {...field}
                placeholder="Select show"
              />
            )}
          />
        </div>
      )}

      {!isMoodFactory && FEATURES.PLAYLIST_JINGLE && hasVisibleJingles && (
        <div style={{ marginBottom: theme.margins.medium }}>
          <Controller
            name="jingle"
            control={control}
            render={({ field }) => (
              <AppSelectDropdown
                searching={fetchingJingles}
                setSearchText={setSearchJingle}
                searchText={searchJingle}
                options={jingles ?? []}
                labelAccessor="title"
                valueAccessor="documentId"
                successStyle={successCreatingPlaylists}
                label={"Jingle"}
                control={control}
                errors={errors}
                {...field}
                value={field.value ?? ""}
                // The jingle is optional, so a pick has to be undoable.
                resetValue={() => field.onChange("")}
                placeholder="Select jingle"
              />
            )}
          />
        </div>
      )}

      {isMoodFactory && (
        <div style={{ marginBottom: theme.margins.medium }}>
          <Controller
            name="moodFactory"
            control={control}
            render={({ field }) => (
              <AppSelectDropdown
                searching={loadingMyShows}
                setSearchText={setSearchText}
                searchText={searchText}
                options={moodFactoryOptions}
                labelAccessor="title"
                valueAccessor="documentId"
                successStyle={successCreatingPlaylists}
                label={"Mood"}
                control={control}
                errors={errors}
                {...field}
                placeholder="Select mood"
              />
            )}
          />
        </div>
      )}

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="playlistName"
          control={control}
          render={({ field }) => (
            <AppInput
              successStyle={successCreatingPlaylists}
              label={"Playlist name"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Enter playlist name"
            />
          )}
        />
      </div>

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <AppInput
              successStyle={successCreatingPlaylists}
              label={"Description"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Overwrite Show Description"
              overrideBorderBottomColor={theme.colors.gray150}
            />
          )}
        />
      </div>

      {!curator?.isSubcurator && (
        <div style={{ marginBottom: theme.margins.medium }}>
          <Controller
            name="assignedGuest"
            control={control}
            render={({ field }) => (
              <AppSelectDropdown
                overrideBorderBottomColor={theme.colors.gray150}
                searching={fetchingMyCurators}
                setSearchText={setSearchGuest}
                searchText={searchGuest}
                options={myCurators}
                labelAccessor="name"
                valueAccessor="documentId"
                successStyle={successCreatingPlaylists}
                label={
                  role === "admin" || role === "manager" ? "Curator" : "Guest"
                }
                control={control}
                errors={errors}
                {...field}
                value={field.value ?? undefined}
                resetValue={() => field.onChange("")}
                placeholder={
                  role === "admin" || role === "manager"
                    ? "Select curator"
                    : "Select guest"
                }
                renderItem={(option: AppSelectDropdownOption) => {
                  const item = option as IGuestCuratorRecord;
                  return (
                    <CuratorDropdownOption
                      name={item.name}
                      imageUrl={
                        item?.webCuratorCatalog?.formats?.thumbnail?.url
                      }
                    />
                  );
                }}
              />
            )}
          />
        </div>
      )}

      <div style={{ marginBottom: theme.margins.medium }}>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <AppInput
              successStyle={successCreatingPlaylists}
              label={"Notes"}
              control={control}
              errors={errors}
              {...field}
              placeholder="Enter any notes"
              overrideBorderBottomColor={theme.colors.gray150}
            />
          )}
        />
      </div>
    </form>
  );
};

export default AddNewPlaylistForm;
